// Confidence Preview modal — Free tier, 90-second micro-check at day 14.
// 3 chip+other questions. On completion, transitions to OfferCard.
//
// Source: CogniPrint_Recalibration_Cadence_Research_v2.md §2.3a,
// CogniPrint_Recalibration_Dev_Plan_v1.md §3.4 (REC-020).

import { useEffect, useState } from 'react';
import { X } from 'lucide-react';
import {
  startConfidencePreview,
  completeConfidencePreview,
  type PreviewQuestion,
  type PreviewAnswer,
} from '@shared/services/recalibration-api';
import type { Day14Offer } from '@shared/types';
import { trackEvent, getBaseProperties } from '@shared/services/analytics';
import { ANALYTICS_EVENTS } from '@shared/constants';
import { OfferCard } from './OfferCard';

interface Props {
  open: boolean;
  onClose: () => void;
}

type Phase =
  | 'loading'
  | 'questions'
  | 'submitting'
  | 'offer'
  | 'already_completed'
  | 'error';

export function PreviewModal({ open, onClose }: Props) {
  const [phase, setPhase] = useState<Phase>('loading');
  const [error, setError] = useState<string | null>(null);
  const [sessionId, setSessionId] = useState<string | null>(null);
  const [dimensionDisplay, setDimensionDisplay] = useState<string>('');
  const [questions, setQuestions] = useState<PreviewQuestion[]>([]);
  const [answers, setAnswers] = useState<Record<string, PreviewAnswer>>({});
  const [activeIndex, setActiveIndex] = useState(0);
  const [headline, setHeadline] = useState<string>('');
  const [offer, setOffer] = useState<Day14Offer | null>(null);

  useEffect(() => {
    if (!open) return;
    let cancelled = false;

    async function start() {
      setPhase('loading');
      setError(null);
      try {
        trackEvent({
          name: ANALYTICS_EVENTS.RECAL_PREVIEW_STARTED,
          properties: getBaseProperties(),
        });
        const result = await startConfidencePreview();
        if (cancelled) return;

        if (result.status === 'preview_already_completed' && result.offer) {
          setOffer(result.offer);
          setHeadline('');
          setPhase('already_completed');
          return;
        }

        if (!result.questions || !result.sessionId || !result.weakestDimension) {
          setPhase('error');
          setError('Preview could not be started. Please try again.');
          return;
        }

        setSessionId(result.sessionId);
        setDimensionDisplay(result.weakestDimension.displayName);
        setQuestions(result.questions);
        setActiveIndex(0);
        setAnswers({});
        setPhase('questions');
      } catch (err) {
        if (cancelled) return;
        setPhase('error');
        setError(err instanceof Error ? err.message : 'Unexpected error');
      }
    }

    void start();
    return () => {
      cancelled = true;
    };
  }, [open]);

  if (!open) return null;

  const activeQuestion = questions[activeIndex];
  const activeAnswer = activeQuestion ? answers[activeQuestion.id] : null;
  const hasSelection =
    (activeAnswer?.chipSelections.length ?? 0) > 0 ||
    (activeAnswer?.freeText?.trim().length ?? 0) > 0;

  function toggleChip(chip: string) {
    if (!activeQuestion) return;
    setAnswers((prev) => {
      const existing = prev[activeQuestion.id] ?? {
        questionId: activeQuestion.id,
        chipSelections: [],
        freeText: null,
      };
      const chipSelections = existing.chipSelections.includes(chip)
        ? existing.chipSelections.filter((c) => c !== chip)
        : [...existing.chipSelections, chip];
      return { ...prev, [activeQuestion.id]: { ...existing, chipSelections } };
    });
  }

  function setFreeText(text: string) {
    if (!activeQuestion) return;
    setAnswers((prev) => {
      const existing = prev[activeQuestion.id] ?? {
        questionId: activeQuestion.id,
        chipSelections: [],
        freeText: null,
      };
      return {
        ...prev,
        [activeQuestion.id]: { ...existing, freeText: text.trim() || null },
      };
    });
  }

  async function onNext() {
    if (activeIndex < questions.length - 1) {
      setActiveIndex(activeIndex + 1);
      return;
    }
    if (!sessionId) return;
    setPhase('submitting');
    try {
      const result = await completeConfidencePreview({
        sessionId,
        answers: questions.map(
          (q) =>
            answers[q.id] ?? {
              questionId: q.id,
              chipSelections: [],
              freeText: null,
            },
        ),
      });
      setHeadline(result.headline);
      setOffer(result.offer);
      trackEvent({
        name: ANALYTICS_EVENTS.RECAL_PREVIEW_COMPLETED,
        properties: { ...getBaseProperties(), dimension: dimensionDisplay },
      });
      trackEvent({
        name: ANALYTICS_EVENTS.RECAL_PREVIEW_HEADLINE_SHOWN,
        properties: { ...getBaseProperties(), headline: result.headline },
      });
      setPhase('offer');
    } catch (err) {
      setPhase('error');
      setError(err instanceof Error ? err.message : 'Could not save preview');
    }
  }

  return (
    <div className="fixed inset-0 z-[70] flex items-center justify-center px-4">
      <div
        className="absolute inset-0"
        onClick={onClose}
        style={{ background: 'rgba(0,0,0,0.6)', backdropFilter: 'blur(6px)' }}
      />
      <div
        className="relative w-full max-w-md rounded-2xl overflow-hidden"
        style={{
          background: 'linear-gradient(180deg, #111528, #0B0E1A)',
          border: '1px solid #2A2F4F',
        }}
      >
        <button
          onClick={onClose}
          className="absolute top-4 right-4 w-8 h-8 flex items-center justify-center rounded-lg text-content-muted hover:text-content transition-colors"
          style={{ background: 'rgba(255,255,255,0.04)', border: '1px solid rgba(255,255,255,0.08)' }}
        >
          <X size={15} />
        </button>

        <div style={{ padding: '28px 26px' }}>
          {phase === 'loading' && (
            <div className="py-10 text-center text-content-muted text-sm">
              Preparing your 90-second check…
            </div>
          )}

          {phase === 'error' && (
            <div className="py-10 text-center">
              <p className="text-red-400 text-sm mb-4">{error}</p>
              <button
                onClick={onClose}
                className="text-content-muted underline text-sm"
              >
                Close
              </button>
            </div>
          )}

          {phase === 'questions' && activeQuestion && (
            <>
              <div className="text-[11px] uppercase tracking-wider text-gold-500 font-semibold mb-1">
                Confidence Preview · {activeIndex + 1} of {questions.length}
              </div>
              <h3 className="font-heading text-[17px] font-bold text-white mb-4 leading-snug">
                {activeQuestion.text}
              </h3>

              <div className="flex flex-wrap gap-2 mb-4">
                {activeQuestion.chips.map((chip) => {
                  const selected =
                    activeAnswer?.chipSelections.includes(chip) ?? false;
                  return (
                    <button
                      key={chip}
                      onClick={() => toggleChip(chip)}
                      className="text-xs font-medium rounded-full transition-all duration-200"
                      style={{
                        padding: '8px 14px',
                        background: selected
                          ? 'rgba(108,92,231,0.18)'
                          : 'rgba(255,255,255,0.03)',
                        border: selected
                          ? '1px solid rgba(108,92,231,0.5)'
                          : '1px solid rgba(255,255,255,0.08)',
                        color: selected ? '#A39BE0' : '#c0bdd4',
                      }}
                    >
                      {chip}
                    </button>
                  );
                })}
              </div>

              {activeQuestion.allow_other && (
                <textarea
                  placeholder="Other — tell me more (optional)"
                  value={activeAnswer?.freeText ?? ''}
                  onChange={(e) => setFreeText(e.target.value)}
                  rows={2}
                  className="w-full rounded-lg text-sm p-3 mb-5"
                  style={{
                    background: 'rgba(255,255,255,0.02)',
                    border: '1px solid rgba(255,255,255,0.08)',
                    color: '#e1dfef',
                    resize: 'none',
                  }}
                />
              )}

              <div className="flex items-center justify-between">
                <div className="text-[11px] text-content-dim">
                  ~90 seconds · no profile changes
                </div>
                <button
                  onClick={onNext}
                  disabled={!hasSelection}
                  className="rounded-lg font-semibold text-sm transition-all disabled:opacity-40 disabled:cursor-not-allowed"
                  style={{
                    padding: '9px 18px',
                    background: 'linear-gradient(135deg, #6C5CE7, #A39BE0)',
                    color: '#fff',
                  }}
                >
                  {activeIndex < questions.length - 1 ? 'Next' : 'See result'}
                </button>
              </div>
            </>
          )}

          {phase === 'submitting' && (
            <div className="py-10 text-center text-content-muted text-sm">
              Analysing your answers…
            </div>
          )}

          {(phase === 'offer' || phase === 'already_completed') && offer && (
            <OfferCard
              headline={headline}
              dimensionDisplay={dimensionDisplay}
              offer={offer}
              alreadyCompleted={phase === 'already_completed'}
              onDismiss={onClose}
            />
          )}
        </div>
      </div>
    </div>
  );
}
